import { Fragment, useState } from 'react';
import { Badge, Button, Table } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type {
  QuestionnaireAnalysisStatus,
  QuestionnaireAnswerValue,
  QuestionnaireResponse,
} from '../../../infrastructure/QuestionnaireClient';

interface QuestionnaireResponsesProps {
  responses: QuestionnaireResponse[];
  isLoading: boolean;
}

const statusVariant = (status: QuestionnaireAnalysisStatus): string => {
  if (status === 'forwarded') return 'success';
  if (status === 'failed') return 'danger';
  return 'warning';
};

const answerPreview = (response: QuestionnaireResponse): string => {
  return response.answers
    .slice(0, 3)
    .map(
      (answer) =>
        `${answer.label || answer.questionId}: ${Array.isArray(answer.answer) ? answer.answer.join(', ') : answer.answer}`,
    )
    .join(' | ');
};

const answerValueLabel = (value: QuestionnaireAnswerValue): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
};

export function QuestionnaireResponses({ responses, isLoading }: QuestionnaireResponsesProps) {
  const [expandedResponseId, setExpandedResponseId] = useState<number | null>(null);

  return (
    <section className="border rounded p-3 bg-white mt-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="h5 mb-0">Answers</h2>
        <span className="text-muted small">{isLoading ? 'Loading' : `${responses.length} rows`}</span>
      </div>
      <Table responsive hover size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Actor ID</th>
            <th>Source</th>
            <th>Status</th>
            <th>Answer Preview</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {responses.map((response) => {
            const isExpanded = expandedResponseId === response.id;
            return (
              <Fragment key={response.id}>
                <tr>
                  <td>{new Date(response.submittedAt).toLocaleString()}</td>
                  <td className="font-monospace small">{response.actorId ?? '-'}</td>
                  <td>{response.source}</td>
                  <td>
                    <Badge bg={statusVariant(response.analysisStatus)}>{response.analysisStatus}</Badge>
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 420 }}>
                    {answerPreview(response)}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setExpandedResponseId(isExpanded ? null : response.id)}
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} answer ${response.id}`}
                    >
                      {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </Button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} className="bg-light">
                      <div className="d-flex flex-column gap-2 py-2">
                        {response.answers.map((answer) => (
                          <div className="border rounded bg-white px-3 py-2" key={`${response.id}-${answer.questionId}`}>
                            <div className="fw-semibold">{answer.label ?? answer.questionId}</div>
                            <div className="text-muted small mb-1">{answer.questionId}</div>
                            <div>{answerValueLabel(answer.answer)}</div>
                          </div>
                        ))}
                        {response.analysisError && (
                          <div className="border rounded bg-white px-3 py-2 text-danger">
                            Analysis error: {response.analysisError}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {!isLoading && responses.length === 0 && (
            <tr>
              <td colSpan={6} className="text-muted py-4 text-center">
                No answers
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </section>
  );
}
